import { scenarioActions } from './scenarioActions';
import { entityActions } from './entityActions';
import { businessActions } from './businessActions';
import { getAccountStructure } from './accountActions';
import { reportingActions } from './reportingActions';
import { allocationActions } from './allocationActions';
import { clearAll } from '../slices/assemblySlice';
import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';

/**
 * Reloads what a saved value or formula change affects, so the open table
 * shows it: the account structure after formula changes, and the current
 * scenario (reloaded and recalculated) when the change touched it.
 *
 * Formula changes and new accounts affect every scenario; value changes only
 * the given ones. New entities or scenarios reload the entity tree and the
 * scenario list. Custom-tab changes and account settings only reload the
 * account structure, since they don't change scenario values. Business-case
 * changes and account settings (which include how accounts combine in a
 * business case) reload the business cases and drop assembled totals.
 * Report changes reload the Reporting page's charts and tables. New
 * allocations reload the allocation list, drop assembled totals and
 * recalculate the current scenario, which may be one they write to.
 *
 * @returns true when the current scenario was reloaded and recalculated
 */
export function reloadAfterChange({
    formulasChanged = false,
    accountsAdded = false,
    entitiesChanged = false,
    customTabsChanged = false,
    businessCasesChanged = false,
    accountSettingsChanged = false,
    reportsChanged = false,
    allocationsChanged = false,
    scenarioIds = [],
}) {
    return async (dispatch, getState) => {
        const structureChanged = formulasChanged || accountsAdded;

        if (reportsChanged) {
            await dispatch(reportingActions.getReportingWidgets({ force: true }));
        }

        if (structureChanged || customTabsChanged || accountSettingsChanged) {
            await dispatch(getAccountStructure({ force: true }));
        }
        if (businessCasesChanged || accountSettingsChanged) {
            dispatch(businessActions.clearAssembledBusinessCases());
            await dispatch(businessActions.getBusinessCases());
        }
        if (allocationsChanged) {
            dispatch(businessActions.clearAssembledBusinessCases());
            dispatch(clearAll());
            await dispatch(allocationActions.getAllocations());
        }
        if (entitiesChanged) {
            await Promise.all([
                dispatch(entityActions.getEntities({ force: true })),
                dispatch(scenarioActions.getScenariosList({ force: true })),
            ]);
        }

        const state = getState();
        const currentScenario = state.scenarios.currentScenario;
        const affectsCurrent =
            currentScenario?.scenario_id != null &&
            (structureChanged || allocationsChanged || scenarioIds.map(Number).includes(Number(currentScenario.scenario_id)));

        if (!affectsCurrent) return false;

        // getScenario resets viewCurrencyId; converge restores it from this argument.
        const viewCurrencyId = state.scenarios.viewCurrencyId;

        await dispatch(scenarioActions.getScenario(Number(currentScenario.scenario_id), { force: true }));

        const reloaded = getState().scenarios.currentScenario;
        const accountStructure = getState().accounts.accountStructure;

        if (reloaded && accountStructure) {
            await dispatch(
                scenarioActions.calculateConvergence(
                    reloaded,
                    buildCalculationAccountStructure(accountStructure),
                    getState().allocations.allocations,
                    [],
                    viewCurrencyId ?? reloaded.scenario_currency,
                ),
            );
        }

        return true;
    };
}
